import { LinerLocation, WearReferencePlane } from "features/organizations/asset-sections/models/asset-section-models"
import { CssSVGClasses } from "../editor-utils/editor-types"
import { GetLinerId } from "../editor-utils/id-string-utils"
import linerLocationsStore from "features/organizations/asset-sections/store/liner-locations-store"

/**
 * Maps Wear Reference Plane  to specific elements within an SVG document and updates the state accordingly.
 *
 * This function takes an SVG document, a rendered SVG element, a setter function for Wear Reference Plane state,
 * and a global Wear Reference Plane value. It processes the SVG paths classified as 'planes' and updates
 * their thickness in the state. This function is designed to work within the context of a React application
 * where state management is handled via hooks.
 *
 * @param {Document} svgDocument - The original SVG document to query for path elements.
 * @param {SVGSVGElement} renderedSvg - The rendered SVG element in the DOM.
 * @param {React.Dispatch<React.SetStateAction<LinerLocation[]>>} setSelectedWearReferencePlane -
 *        A React dispatch function to set the state for Wear Reference Plane.
 * @param {number} globalWearReferencePlane - The global Wear Reference Plane value to be applied.
 *
 * The function first collects all path elements from both the original SVG document and the rendered SVG.
 * It then filters these paths to identify those that are classified as 'liners'. For each liner,
 * the function checks if it's also classified as a 'Wear Reference Plane' and updates the class list if necessary.
 * Finally, it updates the Wear Reference Plane state using the provided setter function, either by updating
 * an existing entry or adding a new one.
 *
 * @returns {void} This function does not return a value; it modifies the state and updates class lists directly.
 *
 * Note: This function contains a try-catch block to handle any exceptions that might occur during processing.
 *       It logs any caught errors to the console.
 */
export function MapWearReferencePlane(
    assetSectionId: string,
    svgDocument: Document,
    renderedSvg: SVGSVGElement,
    selectedWearReferencePlane: WearReferencePlane[],
    setSelectedWearReferencePlane: (liners: WearReferencePlane[]) => void,
    globalWearReferencePlane: number,
): void {
    try {
        const docAllPaths = Array.from(svgDocument.querySelectorAll<SVGPathElement>("path"))
        const allPaths = Array.from(renderedSvg.querySelectorAll<SVGPathElement>("path"))
        const docLiners = docAllPaths.filter(path => path.classList.contains(CssSVGClasses.WsLiner))
        const liners = allPaths.filter(path => path.classList.contains(CssSVGClasses.WsLiner))
        let updatedWearReferencePlane = [...selectedWearReferencePlane]
        liners.forEach(liner => {
            const linerReference = GetLinerId(liner.id)

            const docLinerPart = docLiners.filter(path => {
                return path.id === linerReference
            })

            if (!docLinerPart[0]?.classList.contains(CssSVGClasses.WsWearReferencePlaneTemp))
                docLinerPart[0].classList.add(CssSVGClasses.WsWearReferencePlaneTemp)

            if (linerReference) {
                const linerId = linerLocationsStore.GetLinerLocationIdByReference(assetSectionId, linerReference) ?? ""
                const existingWearReferencePlane = updatedWearReferencePlane.findIndex(entry => entry.reference === linerReference)
                if (existingWearReferencePlane !== -1) {
                    updatedWearReferencePlane[existingWearReferencePlane].wallThickness = globalWearReferencePlane
                } else {
                    updatedWearReferencePlane.push({ linerLocationId: linerId, reference: linerReference, wallThickness: globalWearReferencePlane })
                }
            }
        })
        setSelectedWearReferencePlane(updatedWearReferencePlane)
    } catch (e) {
        console.error("Error happen in MapWearReferencePlane", e)
    }
}

export const GetLinerLocationInfo = (linerLocations: LinerLocation[], pathId: string): LinerLocation | null => {
    try {
        const linerName = GetLinerId(pathId)
        if (!linerName) {
            return null
        }
        return linerLocations.find(l => l.reference?.toLowerCase() === linerName.toLowerCase()) || null
    } catch (e) {
        return null
    }
}

export const GetDrawingReference = (linerLocations: LinerLocation[], pathId: string): number => {
    try {
        const linerName = GetLinerId(pathId) as string
        const backEndWearReferencePlane =
            linerLocations?.find(l => l.reference?.toLocaleLowerCase() === linerName?.toLocaleLowerCase())?.wallThickness ?? 0

        if (backEndWearReferencePlane !== 0) {
            return backEndWearReferencePlane
        } else {
            return 0
        }
    } catch (e) {
        console.error("Error happen in GetWearReferencePlane", e)
        return 0
    }
}
